import { useCallback, useEffect, useRef, useState } from "react";
import type {
  CommandResult,
  JoinResult,
  RoomView,
  SessionCredentials,
} from "../game/types";
import { socket } from "./socket";

const SESSION_KEY = "chin:session:v1";

export type ConnectionStatus = "connecting" | "online" | "offline";

export interface Notice {
  id: number;
  kind: "info" | "success" | "warning";
  message: string;
}

function readSession(): SessionCredentials | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SessionCredentials>;
    if (!parsed.roomCode || !parsed.playerId || !parsed.sessionToken) return null;
    return parsed as SessionCredentials;
  } catch {
    return null;
  }
}

function writeSession(session: SessionCredentials | null): void {
  try {
    if (session) localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    else localStorage.removeItem(SESSION_KEY);
  } catch {
    // Private browsing and storage quotas must not prevent a match.
  }
}

function commandId(): string {
  return crypto.randomUUID();
}

function reliableCommand(
  send: (acknowledge: (result: CommandResult) => void) => void,
  receive: (result: CommandResult) => void,
  attemptsLeft = 2,
): void {
  let settled = false;
  const timer = window.setTimeout(() => {
    if (settled) return;
    settled = true;
    if (attemptsLeft > 0) {
      reliableCommand(send, receive, attemptsLeft - 1);
      return;
    }
    receive({ ok: false, code: "ACK_TIMEOUT", message: "La red no confirmó la jugada. Probá otra vez." });
  }, 1_200);

  send((result) => {
    if (settled) return;
    settled = true;
    window.clearTimeout(timer);
    receive(result);
  });
}

export function useGame() {
  const [room, setRoom] = useState<RoomView | null>(null);
  const [connection, setConnection] = useState<ConnectionStatus>("connecting");
  const [latency, setLatency] = useState<number | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [busy, setBusy] = useState(false);
  const [pendingCardId, setPendingCardId] = useState<string | null>(null);
  const sessionRef = useRef<SessionCredentials | null>(readSession());
  const roomRef = useRef<RoomView | null>(null);
  const noticeSequence = useRef(0);

  const showNotice = useCallback((message: string, kind: Notice["kind"] = "warning") => {
    noticeSequence.current += 1;
    setNotice({ id: noticeSequence.current, kind, message });
  }, []);

  useEffect(function subscribeToRealtimeServer() {
    function handleState(nextRoom: RoomView) {
      const enteredPlayingState = roomRef.current?.phase !== "playing" && nextRoom.phase === "playing";
      roomRef.current = nextRoom;
      setRoom(nextRoom);
      setPendingCardId(null);
      if (enteredPlayingState) setNotice(null);
    }

    function resumeSession() {
      const session = sessionRef.current;
      if (!session) return;

      socket.emit("room:resume", session, (result) => {
        if (result.ok && result.state) {
          handleState(result.state);
          return;
        }

        sessionRef.current = null;
        writeSession(null);
        roomRef.current = null;
        setRoom(null);
        if (result.message) showNotice(result.message);
      });
    }

    function handleConnect() {
      setConnection("online");
      resumeSession();
    }

    function handleDisconnect() {
      setConnection("offline");
    }

    function handleConnectError() {
      setConnection("offline");
    }

    function handleNotice(nextNotice: Omit<Notice, "id">) {
      showNotice(nextNotice.message, nextNotice.kind);
    }

    function handleFeedback(result: CommandResult) {
      setPendingCardId(null);
      if (!result.ok && result.message) showNotice(result.message);
    }

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.on("connect_error", handleConnectError);
    socket.on("room:state", handleState);
    socket.on("room:notice", handleNotice);
    socket.on("game:feedback", handleFeedback);
    socket.connect();

    return function unsubscribeFromRealtimeServer() {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off("connect_error", handleConnectError);
      socket.off("room:state", handleState);
      socket.off("room:notice", handleNotice);
      socket.off("game:feedback", handleFeedback);
    };
  }, [showNotice]);

  useEffect(function measureRoundTripTime() {
    function ping() {
      if (!socket.connected) return;
      const startedAt = performance.now();
      socket.emit("network:ping", Date.now(), () => {
        setLatency(Math.round(performance.now() - startedAt));
      });
    }

    ping();
    const timer = window.setInterval(ping, 3_000);
    return function stopMeasuringRoundTripTime() {
      window.clearInterval(timer);
    };
  }, []);

  useEffect(function dismissNotices() {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(null), 3_200);
    return function clearNoticeTimer() {
      window.clearTimeout(timer);
    };
  }, [notice]);

  const acceptJoin = useCallback((result: JoinResult): JoinResult => {
    setBusy(false);
    if (result.ok && result.session && result.state) {
      sessionRef.current = result.session;
      writeSession(result.session);
      roomRef.current = result.state;
      setRoom(result.state);
    } else if (result.message) {
      showNotice(result.message);
    }
    return result;
  }, [showNotice]);

  const createRoom = useCallback((name: string): Promise<JoinResult> => {
    setBusy(true);
    return new Promise((resolve) => {
      socket.emit("room:create", { name }, (result) => resolve(acceptJoin(result)));
    });
  }, [acceptJoin]);

  const joinRoom = useCallback((code: string, name: string): Promise<JoinResult> => {
    setBusy(true);
    return new Promise((resolve) => {
      socket.emit("room:join", { code, name }, (result) => resolve(acceptJoin(result)));
    });
  }, [acceptJoin]);

  const ready = useCallback(() => {
    const payload = { commandId: commandId() };
    reliableCommand((acknowledge) => socket.emit("room:ready", payload, acknowledge), (result) => {
      if (!result.ok && result.message) showNotice(result.message);
    });
  }, [showNotice]);

  const play = useCallback((cardId: string, pileIndex: 0 | 1) => {
    const currentRoom = roomRef.current;
    const top = currentRoom?.piles[pileIndex].top;
    if (!currentRoom || !top) return;

    setPendingCardId(cardId);
    const payload = { commandId: commandId(), cardId, pileIndex, expectedTopCardId: top.id };
    reliableCommand(
      (acknowledge) => socket.emit("game:play", payload, acknowledge),
      (result) => {
        if (!result.ok) {
          setPendingCardId(null);
          if (result.message) showNotice(result.message);
        }
      },
    );
  }, [showNotice]);

  const chin = useCallback(() => {
    const currentRoom = roomRef.current;
    if (!currentRoom) return;
    const payload = {
      commandId: commandId(),
      revision: currentRoom.revision,
      seenTopCardIds: [
        currentRoom.piles[0].top?.id ?? null,
        currentRoom.piles[1].top?.id ?? null,
      ] as [string | null, string | null],
    };
    reliableCommand(
      (acknowledge) => socket.emit("game:chin", payload, acknowledge),
      (result) => {
        if (!result.ok && result.message) showNotice(result.message);
      },
    );
  }, [showNotice]);

  const refresh = useCallback(() => {
    const payload = { commandId: commandId() };
    reliableCommand((acknowledge) => socket.emit("game:refresh", payload, acknowledge), (result) => {
      if (!result.ok && result.message) showNotice(result.message);
    });
  }, [showNotice]);

  const leave = useCallback(() => {
    socket.emit("room:leave", () => undefined);
    sessionRef.current = null;
    writeSession(null);
    roomRef.current = null;
    setRoom(null);
    setPendingCardId(null);
    const url = new URL(window.location.href);
    url.searchParams.delete("sala");
    window.history.replaceState({}, "", url);
  }, []);

  return {
    room,
    connection,
    latency,
    notice,
    busy,
    pendingCardId,
    createRoom,
    joinRoom,
    ready,
    play,
    chin,
    refresh,
    leave,
    dismissNotice: () => setNotice(null),
    showNotice,
  };
}
